// KOA-5857 — Pure helpers for binary-typed cells (Image, Signature,
// FileContent, ByteArray, VarBinary, Binary, Bytea).
//
// Extracted so the generic BinaryCellEditor and the image-specific
// ImageCellEditor can both compute size / MIME without one importing
// from the other. Pure, no React, no DOM — testable in isolation.

// Approximates the decoded byte size of a base64 payload without
// allocating the bytes — useful for the compact "type · size" pill on
// a binary cell. Exact size depends on padding bytes; this is within
// 1-2 bytes of the true size, which is plenty for a display hint.
export const approxBase64Bytes = (raw: string): number => {
  if (raw.length === 0) {
    return 0;
  }
  const padding: number = raw.endsWith("==") ? 2 : raw.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((raw.length * 3) / 4) - padding);
};

export const formatByteSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Magic-byte → MIME detection from the start of a base64 payload.
// Browsers happily sniff a wrong MIME on a data URI, so this is more
// about emitting an honest type for the network panel / copy-as-URI
// path than about getting pixels on screen.
//
//   iVBORw0KGgo  — PNG signature (89 50 4E 47 0D 0A 1A 0A) base64'd
//   /9j/         — JPEG SOI marker (FF D8 FF)
//   R0lGOD       — GIF87a / GIF89a header
//   UklGR        — RIFF container (WebP lives inside RIFF)
//   Qk           — BMP "BM" header
//   PHN2Z / PD94 — SVG (`<svg` and `<?xml `)
//
// Fallback to image/png — every browser sniffs from the bytes anyway.
export const detectImageMime = (b64: string): string => {
  if (b64.startsWith("iVBORw0KGgo")) {
    return "image/png";
  }
  if (b64.startsWith("/9j/")) {
    return "image/jpeg";
  }
  if (b64.startsWith("R0lGOD")) {
    return "image/gif";
  }
  if (b64.startsWith("UklGR")) {
    return "image/webp";
  }
  if (b64.startsWith("Qk")) {
    return "image/bmp";
  }
  if (b64.startsWith("PHN2Z") || b64.startsWith("PD94bWwg")) {
    return "image/svg+xml";
  }
  return "image/png";
};

// Categorised description of a binary payload — used by the binary
// cell chip to show "PDF · 1.2 MB" instead of "bytea · 1.2 MB".
// Detection is best-effort: only the common container formats whose
// magic bytes are stable. Unknown payloads come back as "Binary".
export interface IDetectedBinary {
  mime: string;
  label: string;
  ext: string;
}

// Decode the first `count` bytes of a base64 payload to a Uint8Array,
// or null if the input is unusable. Used by format detectors that
// can't be done via b64 prefix matching alone — base64 packs every 3
// bytes into 4 chars, so any feature whose byte offset isn't a
// multiple of 3 produces a b64 substring that depends on the
// surrounding bytes. The MP4 `ftyp` atom at byte 4 is the canonical
// example: it would never be reliably caught by a substring search.
const decodeFirstBytes = (b64: string, count: number): Uint8Array | null => {
  if (b64.length === 0) {
    return null;
  }
  // Round to a whole base64 block (4 chars → 3 bytes). Pad if the
  // user payload was truncated mid-block so atob doesn't throw.
  const charsNeeded: number = Math.ceil(count / 3) * 4;
  const slice: string = b64.slice(0, Math.min(charsNeeded, b64.length));
  const padCount: number = (4 - (slice.length % 4)) % 4;
  const padded: string = slice + "=".repeat(padCount);
  try {
    const decoded: string = atob(padded);
    const bytes: Uint8Array = new Uint8Array(decoded.length);
    for (let i: number = 0; i < decoded.length; i += 1) {
      bytes[i] = decoded.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
};

// MP4 / MOV / M4A / M4V header: 4-byte size atom, then ASCII "ftyp",
// then a brand. We check the 4 magic bytes at offset 4 directly,
// which catches every real ISO BMFF file regardless of how the size
// atom packs into base64.
const isMp4Ftyp = (b64: string): boolean => {
  const bytes: Uint8Array | null = decodeFirstBytes(b64, 12);
  if (bytes === null || bytes.length < 8) {
    return false;
  }
  return bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
};

export const detectBinaryKind = (b64: string): IDetectedBinary => {
  if (b64.length === 0) {
    return { mime: "application/octet-stream", label: "Binary", ext: "bin" };
  }
  // Images go through ImageCellEditor, but the binary editor still
  // covers FileContent / ByteArray etc. where someone might have
  // stuffed an image. Recognise the common ones so the chip reads
  // honestly.
  if (b64.startsWith("iVBORw0KGgo")) {
    return { mime: "image/png", label: "PNG", ext: "png" };
  }
  if (b64.startsWith("/9j/")) {
    return { mime: "image/jpeg", label: "JPEG", ext: "jpg" };
  }
  if (b64.startsWith("R0lGOD")) {
    return { mime: "image/gif", label: "GIF", ext: "gif" };
  }
  // %PDF-
  if (b64.startsWith("JVBERi0")) {
    return { mime: "application/pdf", label: "PDF", ext: "pdf" };
  }
  // ZIP-family — also covers .docx / .xlsx / .pptx / .jar / .apk.
  //
  // Bytes: 50 4B (PK) followed by 03/05/07 (entry-type byte). Base64
  // packs 3 input bytes into 4 output chars, so the FIRST FOUR base64
  // chars exactly cover those three signature bytes — independent of
  // whatever 4th byte follows. That matters because real ZIPs almost
  // always have a 4th byte of 0x04 (local file header) and a 5th byte
  // of 0x14 (extract-needed version 2.0), which together produce the
  // base64 prefix `UEsDBB` — not the `UEsDBA` an earlier version of
  // this detector matched on. `UEsDBA` only fires when the 5th byte
  // is 0x00, which is basically never real:
  //
  //   UEsD  — PK\x03 — local file header (almost every ZIP)
  //   UEsF  — PK\x05 — end of central directory (empty ZIPs)
  //   UEsH  — PK\x07 — data descriptor (basically never first)
  if (b64.startsWith("UEsD") || b64.startsWith("UEsF") || b64.startsWith("UEsH")) {
    return { mime: "application/zip", label: "ZIP", ext: "zip" };
  }
  // 7z signature — bytes `37 7A BC AF 27 1C`. Base64-encoding the
  // first 3 of those (37 7A BC) gives `N3q8`; the prior `N3p8`
  // here was off by one character of the base64 alphabet
  // (`p`=41 vs `q`=42 in standard b64), which meant every real
  // 7z archive fell through to "Binary" while a fake payload
  // starting with `N3p8` was getting tagged as 7z.
  if (b64.startsWith("N3q8")) {
    return { mime: "application/x-7z-compressed", label: "7z", ext: "7z" };
  }
  // Rar!
  if (b64.startsWith("UmFy")) {
    return { mime: "application/vnd.rar", label: "RAR", ext: "rar" };
  }
  // GZip
  if (b64.startsWith("H4sI")) {
    return { mime: "application/gzip", label: "GZip", ext: "gz" };
  }
  // ID3 / MPEG frame headers (mp3)
  if (b64.startsWith("SUQz") || b64.startsWith("//s") || b64.startsWith("//t") || b64.startsWith("//v")) {
    return { mime: "audio/mpeg", label: "MP3", ext: "mp3" };
  }
  // OggS
  if (b64.startsWith("T2dnUw")) {
    return { mime: "audio/ogg", label: "OGG", ext: "ogg" };
  }
  // fLaC
  if (b64.startsWith("ZkxhQw")) {
    return { mime: "audio/flac", label: "FLAC", ext: "flac" };
  }
  // EBML (WebM / MKV)
  if (b64.startsWith("GkXfo")) {
    return { mime: "video/webm", label: "WebM", ext: "webm" };
  }
  // MP4 / MOV / M4A — checked at the byte level via `isMp4Ftyp` since
  // the `ftyp` atom usually sits at byte 4, which doesn't align to a
  // base64 block boundary and so can't be reliably matched as a b64
  // substring. The previous indexOf-based check both missed real
  // MP4s AND falsely matched unknown payloads (indexOf returns -1,
  // which passed the upper bound).
  if (isMp4Ftyp(b64)) {
    return { mime: "video/mp4", label: "MP4", ext: "mp4" };
  }
  // OLE compound — legacy Office docs (.doc / .xls / .ppt) and MSI.
  if (b64.startsWith("0M8R4KGxGuE")) {
    return { mime: "application/octet-stream", label: "Office", ext: "bin" };
  }
  // SVG starts with a literal `<svg` or `<?xml`.
  if (b64.startsWith("PHN2Z") || b64.startsWith("PD94bWwg")) {
    return { mime: "image/svg+xml", label: "SVG", ext: "svg" };
  }
  // BMP "BM"
  if (b64.startsWith("Qk")) {
    return { mime: "image/bmp", label: "BMP", ext: "bmp" };
  }
  // RIFF — could be WebP or WAV; without decoding more bytes we can
  // only say it's a RIFF container.
  if (b64.startsWith("UklGR")) {
    return { mime: "application/octet-stream", label: "RIFF", ext: "bin" };
  }
  return { mime: "application/octet-stream", label: "Binary", ext: "bin" };
};

// Best-effort file extension from a MIME type. Used to pick a sensible
// suffix when offering a binary value as a download — `image-foo.png`
// reads cleaner than `image-foo.bin`. Returns `bin` for unknown MIMEs
// so the browser still saves something.
export const extensionForMime = (mime: string): string => {
  switch (mime) {
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "image/gif":
      return "gif";
    case "image/webp":
      return "webp";
    case "image/bmp":
      return "bmp";
    case "image/svg+xml":
      return "svg";
    default:
      return "bin";
  }
};

// Short, file-name-safe identifier for a row's primary key. Used to
// disambiguate downloads from multiple rows of the same table —
// without this every cell's download lands as `<column>.<ext>` and
// repeat downloads stack with `(1)`, `(2)` … in the browser's
// Downloads folder.
//
// Heuristics:
//   - GUID-shaped (`8d71b9f4-…`): first 8 hex chars. Long enough to
//     stay unique within any reasonable table, short enough to read.
//   - Numeric (autoincrement / int PK): the full number.
//   - Anything else: first 12 chars run through the filename
//     sanitiser to strip path-illegal characters.
// Empty / null input returns empty string so callers can fold it
// into a `${base}${short ? "-" + short : ""}` template cleanly.
export const shortRowKeyForFilename = (rowKey: string | null | undefined): string => {
  if (typeof rowKey !== "string" || rowKey.length === 0) {
    return "";
  }
  const guidHead: RegExp = /^([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const guidMatch: RegExpExecArray | null = guidHead.exec(rowKey);
  if (guidMatch !== null) {
    return guidMatch[1]!.toLowerCase();
  }
  if (/^-?\d+$/.test(rowKey)) {
    return rowKey;
  }
  const clean: string = sanitiseFileNamePart(rowKey);
  return clean.length > 12 ? clean.slice(0, 12) : clean;
};

// Browser-safe file-name sanitiser. Database identifiers can hold
// characters that are illegal in file paths on Windows (`<>:"/\|?*`)
// and confusing on macOS / Linux (newlines, control chars). We don't
// know the user's OS, so strip anything that isn't a letter, digit,
// dash, underscore, or dot. Collapses runs of `_` for readability and
// caps the result so the suggested filename stays sane.
export const sanitiseFileNamePart = (raw: string): string => {
  if (raw.length === 0) {
    return "value";
  }
  const cleaned: string = raw
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (cleaned.length === 0) {
    return "value";
  }
  return cleaned.length > 60 ? cleaned.slice(0, 60) : cleaned;
};

// ── Image content sniffing ─────────────────────────────────────────
//
// The HTML `accept="image/*"` filter and `file.type` are *metadata*
// — a renamed `.png` file passes both. For data quality we want to
// verify the actual bytes look like an image format before storing.
//
// Magic-byte predicates below take a Uint8Array of the first ~16
// file bytes. SVG is the one common image format that isn't binary
// magic — it's text — so it gets a separate text path.

const isPngBytes = (b: Uint8Array): boolean => {
  return (
    b.length >= 8 &&
    b[0] === 0x89 &&
    b[1] === 0x50 &&
    b[2] === 0x4e &&
    b[3] === 0x47 &&
    b[4] === 0x0d &&
    b[5] === 0x0a &&
    b[6] === 0x1a &&
    b[7] === 0x0a
  );
};

const isJpegBytes = (b: Uint8Array): boolean => {
  return b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
};

const isGifBytes = (b: Uint8Array): boolean => {
  // GIF87a or GIF89a
  return (
    b.length >= 6 &&
    b[0] === 0x47 &&
    b[1] === 0x49 &&
    b[2] === 0x46 &&
    b[3] === 0x38 &&
    (b[4] === 0x37 || b[4] === 0x39) &&
    b[5] === 0x61
  );
};

const isWebpBytes = (b: Uint8Array): boolean => {
  // RIFF....WEBP — the four-byte size word between RIFF and WEBP
  // depends on the file, so we check bytes 0-3 and 8-11 only.
  return (
    b.length >= 12 &&
    b[0] === 0x52 &&
    b[1] === 0x49 &&
    b[2] === 0x46 &&
    b[3] === 0x46 &&
    b[8] === 0x57 &&
    b[9] === 0x45 &&
    b[10] === 0x42 &&
    b[11] === 0x50
  );
};

const isBmpBytes = (b: Uint8Array): boolean => {
  return b.length >= 2 && b[0] === 0x42 && b[1] === 0x4d;
};

// SVG isn't byte-magic; it's text. Strip an optional UTF-8 BOM and
// leading whitespace, then accept either a bare `<svg` opener or an
// XML prologue that precedes one. Matching is anchored so a `<svg`
// buried inside an HTML or arbitrary text file doesn't false-pass.
export const isSvgText = (text: string): boolean => {
  const stripped: string = text.replace(/^﻿/, "").trim();
  if (/^<svg\b/i.test(stripped)) {
    return true;
  }
  return /^<\?xml\b[\s\S]*?<svg\b/i.test(stripped);
};

// Best-effort content sniff for an image File. Returns the detected
// MIME (one of image/png, image/jpeg, image/gif, image/webp,
// image/bmp, image/svg+xml) or null when the bytes don't match any
// recognised image format. The full base64 read can then run only on
// payloads that actually look like images — a renamed
// `payload.exe → photo.png` is rejected before storage.
export const sniffImageFile = async (file: File): Promise<string | null> => {
  // SVG candidate: MIME or `.svg` extension hint. (`.svgz` — gzipped
  // SVG — we don't support, since browser-side gunzip would need an
  // extra dep just for the rare case.)
  const looksSvgHint: boolean = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
  if (looksSvgHint) {
    try {
      const head: string = await file.slice(0, 512).text();
      if (isSvgText(head)) {
        return "image/svg+xml";
      }
    } catch {
      return null;
    }
    return null;
  }
  try {
    const buffer: ArrayBuffer = await file.slice(0, 16).arrayBuffer();
    const bytes: Uint8Array = new Uint8Array(buffer);
    if (isPngBytes(bytes)) {
      return "image/png";
    }
    if (isJpegBytes(bytes)) {
      return "image/jpeg";
    }
    if (isGifBytes(bytes)) {
      return "image/gif";
    }
    if (isWebpBytes(bytes)) {
      return "image/webp";
    }
    if (isBmpBytes(bytes)) {
      return "image/bmp";
    }
  } catch {
    return null;
  }
  return null;
};

// ── Data-URI helpers ───────────────────────────────────────────────
//
// Image / Signature columns at runtime store the FULL data URI
// (`data:image/png;base64,…`) because the runtime `Image` and
// `Signature` components feed the value straight into `<img src>` /
// `signaturePad.fromDataURL`. The table editor's previous wire
// format was body-only base64 (no `data:` prefix), which renders
// nothing in the runtime app even though it looks fine in the
// editor's own thumbnail. These helpers let the editor keep its
// rendering paths working whether the value on disk is the legacy
// body-only form or the new data-URI form.

export const isDataUri = (s: string): boolean => {
  return s.startsWith("data:") && s.indexOf(";base64,") > 5;
};

// Strip the `data:<mime>;base64,` prefix and return just the
// payload. Idempotent — passing already-stripped base64 returns it
// unchanged.
export const stripDataUriPrefix = (s: string): string => {
  if (!isDataUri(s)) {
    return s;
  }
  const commaIdx: number = s.indexOf(",");
  return commaIdx >= 0 ? s.slice(commaIdx + 1) : s;
};

// Pull the MIME out of a data URI. Returns null when the input
// isn't a data URI — the caller can fall back to byte-sniffing the
// payload (`detectImageMime`).
export const extractDataUriMime = (s: string): string | null => {
  if (!isDataUri(s)) {
    return null;
  }
  const semicolonIdx: number = s.indexOf(";");
  if (semicolonIdx <= 5) {
    return null;
  }
  return s.slice(5, semicolonIdx);
};

// ── file-type integration (lazy-loaded) ────────────────────────────
//
// The hand-rolled `detectBinaryKind` covers the common formats with
// short string-prefix checks, but it ships subtle base64-alignment
// bugs (the ZIP and 7z prefixes both shipped wrong in this session
// before someone uploaded a real file). The `file-type` npm package
// is the maintained source of truth — Sindre Sorhus's library that
// recognises ~500 formats from real magic-byte sniffs on raw bytes.
//
// We dynamic-import it so the ~50 KB bundle isn't paid by anyone
// who doesn't actually touch a binary cell. Cached at module scope
// after the first load. Returns null on import failure (offline /
// blocked) so the caller falls back to the hand-rolled detector.

interface IFileTypeModule {
  fileTypeFromBuffer: (buf: Uint8Array) => Promise<{ ext: string; mime: string } | undefined>;
}
let _fileTypeModule: IFileTypeModule | null = null;
let _fileTypeLoad: Promise<IFileTypeModule | null> | null = null;

const loadFileTypeModule = async (): Promise<IFileTypeModule | null> => {
  if (_fileTypeModule !== null) {
    return _fileTypeModule;
  }
  if (_fileTypeLoad !== null) {
    return _fileTypeLoad;
  }
  _fileTypeLoad = import("file-type")
    .then((mod: unknown): IFileTypeModule | null => {
      const m: { fileTypeFromBuffer?: IFileTypeModule["fileTypeFromBuffer"] } = mod as {
        fileTypeFromBuffer?: IFileTypeModule["fileTypeFromBuffer"];
      };
      if (typeof m.fileTypeFromBuffer !== "function") {
        return null;
      }
      _fileTypeModule = { fileTypeFromBuffer: m.fileTypeFromBuffer };
      return _fileTypeModule;
    })
    .catch((): null => null);
  return _fileTypeLoad;
};

// Decode the first N base64 chars into bytes for file-type sniffing.
// file-type only needs the first ~4 KB for any format it knows, so
// decoding the whole payload (potentially many MB) would waste cycles.
const decodeBase64Head = (b64: string, byteCount: number): Uint8Array | null => {
  const charsNeeded: number = Math.ceil(byteCount / 3) * 4;
  const slice: string = b64.slice(0, Math.min(charsNeeded, b64.length));
  const padCount: number = (4 - (slice.length % 4)) % 4;
  const padded: string = slice + "=".repeat(padCount);
  try {
    const decoded: string = atob(padded);
    const bytes: Uint8Array = new Uint8Array(decoded.length);
    for (let i: number = 0; i < decoded.length; i += 1) {
      bytes[i] = decoded.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
};

// Detect a binary payload's MIME / extension using the file-type
// library when available, falling back to the hand-rolled
// `detectBinaryKind` otherwise. Async because file-type is dynamic-
// imported; callers should hold the chip label in state and update
// when the promise resolves.
export const detectBinaryKindAsync = async (b64: string): Promise<IDetectedBinary> => {
  if (b64.length === 0) {
    return { mime: "application/octet-stream", label: "Binary", ext: "bin" };
  }
  const mod: IFileTypeModule | null = await loadFileTypeModule();
  if (mod !== null) {
    const head: Uint8Array | null = decodeBase64Head(b64, 4100);
    if (head !== null) {
      const sniffed: { ext: string; mime: string } | undefined = await mod.fileTypeFromBuffer(head);
      if (sniffed !== undefined) {
        return {
          mime: sniffed.mime,
          ext: sniffed.ext,
          label: sniffed.ext.toUpperCase()
        };
      }
    }
  }
  // file-type didn't recognise the payload (or failed to load) —
  // fall back to the hand-rolled detector for the common cases it
  // still covers.
  return detectBinaryKind(b64);
};

// Reads a File as a complete data URI (with `data:<mime>;base64,`
// prefix). This is the format the runtime Image / Signature
// components consume — and that the runtime's own image-input
// widgets (md-editor image insert, signature canvas) write back.
// Used by the image cell editor after content sniffing has
// validated the bytes.
export const readFileAsDataUri = (file: File): Promise<string> => {
  return new Promise((resolve, reject): void => {
    const reader: FileReader = new FileReader();
    reader.onload = (): void => {
      const result: string = typeof reader.result === "string" ? reader.result : "";
      resolve(result);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
};

// Reads a File into the base64 string the rest of Kuika stores (no
// `data:...;base64,` prefix — just the encoded body). Used by the
// binary + image cell editors after their pre-load guards have
// already validated MIME / size.
export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject): void => {
    const reader: FileReader = new FileReader();
    reader.onload = (): void => {
      const result: string = typeof reader.result === "string" ? reader.result : "";
      const commaIdx: number = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
};

// Hard cap on what `downloadBase64` will attempt to decode in one
// shot. `atob` is synchronous and pegs the main thread; new uploads
// are already capped at 50 MB (binary) / 25 MB (image), but rows
// written before those caps existed can be larger. Above this
// ceiling we refuse with a friendly error message instead of
// freezing the editor for the user.
const MAX_DOWNLOAD_BYTES: number = 100 * 1024 * 1024;

export interface IDownloadOutcome {
  ok: boolean;
  // Human-readable message for the caller to surface in the UI. Only
  // populated when ok === false.
  errorMessage?: string;
}

// Triggers a browser download of a base64 payload as a file. Builds an
// object URL from a Blob (rather than handing the `<a>` a giant data:
// URI directly — Chrome refuses anchors with multi-megabyte data URIs
// for security reasons) and revokes it after a microtask so the
// download keeps a live reference.
//
// Returns an outcome rather than throwing so callers don't need to
// wrap with try/catch — they just surface `errorMessage` to the user.
// Three failure modes:
//
//   - `unavailable`  : called from SSR / non-browser context.
//   - `too-large`    : decoded size would exceed the safety cap.
//   - `invalid-base64`: atob threw — most often a row stored some
//                       garbage we can't decode (corrupted bytes,
//                       partially-truncated upload, etc.).
export const downloadBase64 = (b64: string, mime: string, filename: string): IDownloadOutcome => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { ok: false, errorMessage: "Downloads aren't available in this context." };
  }
  // Approximate decoded size from the base64 length — `atob` would
  // produce ~3/4 of the input string in bytes. Cheaper than decoding
  // first and rejecting after.
  const approxBytes: number = approxBase64Bytes(b64);
  if (approxBytes > MAX_DOWNLOAD_BYTES) {
    return {
      ok: false,
      errorMessage: `Value is ${formatByteSize(approxBytes)} — too large to download in the browser (cap ${formatByteSize(
        MAX_DOWNLOAD_BYTES
      )}). Use a server-side export.`
    };
  }
  let binary: string;
  try {
    binary = window.atob(b64);
  } catch {
    return { ok: false, errorMessage: "Stored value isn't valid base64 — can't download." };
  }
  let url: string | null = null;
  let a: HTMLAnchorElement | null = null;
  try {
    const length: number = binary.length;
    const buffer: ArrayBuffer = new ArrayBuffer(length);
    const bytes: Uint8Array = new Uint8Array(buffer);
    for (let i: number = 0; i < length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob: Blob = new Blob([buffer], { type: mime });
    url = URL.createObjectURL(blob);
    a = document.createElement("a");
    a.href = url;
    a.download = filename;
    // Some browsers ignore the click unless the anchor is in the DOM.
    document.body.appendChild(a);
    a.click();
  } catch {
    return { ok: false, errorMessage: "Couldn't start the download." };
  } finally {
    if (a !== null && a.parentNode !== null) {
      a.parentNode.removeChild(a);
    }
    if (url !== null) {
      // Defer the revoke so Safari has time to start the download
      // stream. Capture the function reference BEFORE scheduling so
      // the timer doesn't look up `URL.revokeObjectURL` again at fire
      // time — relevant in tests where the property may be stubbed +
      // deleted in cleanup, and pedantically safer in real browsers
      // where another script could overwrite the slot in between.
      const urlToRevoke: string = url;
      const revoke: typeof URL.revokeObjectURL = URL.revokeObjectURL;
      setTimeout((): void => revoke(urlToRevoke), 0);
    }
  }
  return { ok: true };
};
